import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  LayoutGrid, Users, Store as StoreIcon, BadgeCheck, ShieldHalf, ShoppingBag, Package, ClipboardList,
  Star, LayoutList, Truck, Bike, Settings2, CreditCard, HandCoins, Banknote, QrCode, Send, Undo2, Handshake, Trash2, Plus,
  Percent, Crown, Megaphone, Flag, Scale, MessageSquareWarning, LifeBuoy, BarChart3, KeyRound, ScrollText, Settings, Gift,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import api, { formatApiError } from '../../lib/api';
import { Switch } from '../../components/ui/switch';
import { KpiCard } from '../../components/commerce/KpiCard';
import { StatusBadge } from '../../components/commerce/StatusBadge';
import { EmptyState } from '../../components/commerce/EmptyState';
import { fmtRM2, timeAgo, PLANS } from '../../data/mockData';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Stub, ArrowUpIcon, ArrowDownIcon } from './common';

export function AdminDashboard() {
  const { orders, deliveries, payouts, cashEntries, applications, adminMerchants } = useApp();
  const gmv = orders.filter((o) => o.orderStatus !== 'CANCELLED').reduce((s, o) => s + o.itemSubtotal, 0);
  const commissionRevenue = orders.reduce((s, o) => s + o.commissionAmount, 0);
  const deliveryRevenue = orders.reduce((s, o) => s + o.deliveryFee, 0);
  const pendingPickups = deliveries.filter((d) => d.deliveryStatus === 'WAITING_PICKUP').length;
  const outForDelivery = deliveries.filter((d) => ['OUT_FOR_DELIVERY', 'ARRIVED'].includes(d.deliveryStatus)).length;
  const pendingCollections = orders.filter((o) => ['COLLECTED_BY_DRIVER', 'VERIFICATION_REQUIRED'].includes(o.paymentStatus)).length;
  const payoutsReady = payouts.filter((p) => p.status === 'READY').length;
  const cashWithRiders = cashEntries.filter((c) => ['AWAITING_HANDOVER', 'COLLECTED_BY_DRIVER'].includes(c.status)).reduce((s, c) => s + c.amount, 0);

  const actions = [
    { label: 'Merchant applications', count: applications.filter((a) => a.status === 'PENDING').length, to: '/admin/verification' },
    { label: 'Pickups to assign', count: pendingPickups, to: '/admin/delivery' },
    { label: 'Payments to verify', count: pendingCollections, to: '/admin/finance/digital-collections' },
    { label: 'Payouts ready', count: payoutsReady, to: '/admin/finance/payouts' },
    { label: 'Cash with riders', count: cashWithRiders > 0 ? fmtRM2(cashWithRiders) : 0, to: '/admin/finance/cash', raw: true },
  ];

  return (
    <div data-testid="admin-dashboard" className="space-y-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard label="GMV" value={fmtRM2(gmv)} icon={BarChart3} testId="admin-kpi-gmv" />
        <KpiCard label="Orders" value={orders.length} icon={ClipboardList} testId="admin-kpi-orders" />
        <KpiCard label="Merchants" value={adminMerchants.length} icon={StoreIcon} testId="admin-kpi-merchants" />
        <KpiCard label="Commission Revenue" value={fmtRM2(commissionRevenue)} sub={`Delivery revenue ${fmtRM2(deliveryRevenue)}`} icon={Percent} testId="admin-kpi-revenue" />
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard label="Pending Pickups" value={pendingPickups} icon={Package} testId="admin-kpi-pickups" />
        <KpiCard label="Out for Delivery" value={outForDelivery} icon={Truck} testId="admin-kpi-ofd" />
        <KpiCard label="Pending Collections" value={pendingCollections} icon={HandCoins} testId="admin-kpi-collections" />
        <KpiCard label="Payouts Ready" value={payoutsReady} icon={Send} testId="admin-kpi-payouts" />
      </div>
      <section className="rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-4">
        <h3 className="mb-3 text-sm font-semibold text-kelate-ink">Action Required</h3>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
          {actions.map((a) => (
            <Link key={a.label} to={a.to} data-testid={`action-${a.label.toLowerCase().replace(/\s+/g, '-')}`} className="rounded-sm border border-kelate-line px-3 py-3 transition-colors hover:border-kelate-wood">
              <div className="text-lg font-semibold text-kelate-darkwood">{a.count}</div>
              <div className="text-xs text-kelate-soft">{a.label}</div>
            </Link>
          ))}
        </div>
      </section>
      <section className="rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-4">
        <h3 className="mb-3 text-sm font-semibold text-kelate-ink">Latest Orders</h3>
        <div className="space-y-2">
          {orders.slice(0, 6).map((o) => (
            <div key={o.id} className="flex flex-wrap items-center justify-between gap-2 rounded-sm border border-kelate-line/70 px-3 py-2 text-sm">
              <span className="font-medium text-kelate-ink">#{o.orderNumber} <span className="text-xs font-normal text-kelate-soft">· {o.merchantName}</span></span>
              <span className="flex items-center gap-2">
                <span className="text-xs text-kelate-soft">{fmtRM2(o.totalCustomerDue)}</span>
                <StatusBadge status={o.orderStatus} />
                <StatusBadge status={o.paymentStatus} />
              </span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

